import type { AxiosResponse } from 'axios';
import { BACKEND_ERROR_CODE, createFlatRequest, createRequest } from '@sa/axios';
import { useAuthStore } from '@/store/modules/auth';
import { localStg } from '@/utils/storage';
import { getServiceBaseURL } from '@/utils/service';
import { $t } from '@/locales';
import { getAuthorization, handleExpiredRequest, showErrorMsg } from './shared';
import type { RequestInstanceState } from './type';

const isHttpProxy = import.meta.env.DEV && import.meta.env.VITE_HTTP_PROXY === 'Y';
const { baseURL, otherBaseURL } = getServiceBaseURL(import.meta.env, isHttpProxy);

export const request = createFlatRequest<App.Service.Response, RequestInstanceState>(
  {
    baseURL,
    headers: {
      apifoxToken: 'XL299LiMEDZ0H5h3A29PxwQXdMJqWyY2'
    }
  },
  {
    async onRequest(config) {
      const Authorization = getAuthorization();
      Object.assign(config.headers, { Authorization });

      return config;
    },
    isBackendSuccess(response) {
      // PHP接口成功状态码是0
      return response.data.code === 0;
    },
    async onBackendFail(response, instance) {
      const authStore = useAuthStore();
      const responseCode = response.data.code;
      const requestUrl = response.config.url;

      function handleLogout() {
        console.log('401 handleLogout');
        // 如果是 TOTP 验证请求，不要清除 tempToken
        if (requestUrl?.includes('/admin/verify-totp')) {
          console.log('TOTP verification failed, preserving tempToken');
          return;
        }
        authStore.resetStore();
      }

      function logoutAndCleanup() {
        console.log('401 logoutAndCleanup');
        handleLogout();
        window.removeEventListener('beforeunload', handleLogout);

        request.state.errMsgStack = request.state.errMsgStack.filter(msg => msg !== response.data.msg);
      }

      // 401表示未授权，直接跳转登录页
      if (responseCode === 401) {
        console.log('401 error, redirecting to login', response);
        logoutAndCleanup();
        return null;
      }

      // 处理token过期的情况
      // if (responseCode === 401) {
      //   const success = await handleExpiredRequest(request.state);
      //   if (success) {
      //     const Authorization = getAuthorization();
      //     Object.assign(response.config.headers, { Authorization });
      //     return instance.request(response.config) as Promise<AxiosResponse>;
      //   }else{
      //     handleLogout();
      //     return null;
      //   }
      // }

      // 403表示禁止访问，显示错误信息
      if (responseCode === 403 && !request.state.errMsgStack?.includes(response.data.msg)) {
        request.state.errMsgStack = [...(request.state.errMsgStack || []), response.data.msg];

        window.$dialog?.error({
          title: $t('common.error'),
          content: response.data.msg,
          positiveText: $t('common.confirm'),
          maskClosable: false,
          closeOnEsc: false,
          onPositiveClick() {
            logoutAndCleanup();
          },
          onClose() {
            logoutAndCleanup();
          }
        });

        return null;
      }



      return null;
    },
    transformBackendResponse(response) {
      // 直接返回整个响应，让调用方处理data字段
      return response.data;
    },
    onError(error) {
      // when the request is fail, you can show error message

      let message = error.message;
      let backendErrorCode = '';
      const requestUrl = error.config?.url;

      // get backend error message and code
      if (error.code === BACKEND_ERROR_CODE) {
        message = error.response?.data?.msg || message;
        backendErrorCode = String(error.response?.data?.code || '');
      }

      // 401错误不显示错误信息，因为会被重定向到登录页
      // 但是 TOTP 验证的 401 错误应该显示，因为这是业务错误
      if (backendErrorCode === '401' && !requestUrl?.includes('/admin/verify-totp')) {
        return;
      }

      showErrorMsg(request.state, message);
    }
  }
);

export const demoRequest = createRequest<App.Service.DemoResponse>(
  {
    baseURL: otherBaseURL.demo
  },
  {
    async onRequest(config) {
      const { headers } = config;

      // set token
      const token = localStg.get('token');
      const Authorization = token ? `Bearer ${token}` : null;
      Object.assign(headers, { Authorization });

      return config;
    },
    isBackendSuccess(response) {
      // when the backend response code is "200", it means the request is success
      // you can change this logic by yourself
      return response.data.status === '200';
    },
    async onBackendFail(_response) {
      // when the backend response code is not "200", it means the request is fail
      // for example: the token is expired, refresh token and retry request
    },
    transformBackendResponse(response) {
      return response.data.result;
    },
    onError(error) {
      // when the request is fail, you can show error message

      let message = error.message;

      // show backend error message
      if (error.code === BACKEND_ERROR_CODE) {
        message = error.response?.data?.message || message;
      }

      window.$message?.error(message);
    }
  }
);
